import { TokenAmount } from "@/components/brand/TokenAmount";
import { ConfirmSheet } from "@/components/defi/ConfirmSheet";
import {
	runDemoUpdateStages,
	type UpdateStage,
} from "@/components/defi/UpdateStageLoader";
import { Button } from "@/components/ui/Button";
import { CollateralLines } from "@/components/vault/CollateralLines";
import { VaultRiskBadge } from "@/components/vault/VaultRiskBadge";
import {
	useGetLiquidationAuctions,
	useGetPublicLiquidatableVaults,
	useProtocolRiskParams,
	toRiskParams,
	type LiquidatableVaultUi,
} from "@/hooks/usePublicProtocol";
import {
	useMyAgent,
	useMyHuntHfRange,
	useMyHuntTargets,
	type HuntTargetUi,
} from "@/hooks/useAgentGateway";
import { isAuthPermissionError, useAuth } from "@/lib/auth";
import { depositableTokenMax } from "@/lib/amount-limits";
import { vaultMatchesHfRange, vaultMatchesHunt } from "@/lib/hunt-match";
import { ensureProtocolUser } from "@/lib/ensure-user";
import { humanizeIcError } from "@/lib/ic-errors";
import { getIcConfig } from "@/lib/network";
import { docsArticlePath } from "@/lib/docs/parse";
import { classifyVaultRisk, riskLegend } from "@/lib/vault-risk";
import { cn, formatAmountPlain, formatNum, parseAmount } from "@/lib/utils";
import {
	fillLiquidationAuction,
	liquidateVault,
	unwrapResult,
} from "@motokomaker/ic-client";
import { Principal } from "@icp-sdk/core/principal";
import { Gavel } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { WalletActivityPanel } from "@/components/wallet/WalletActivityPanel";

type Tab = "live" | "auctions" | "history";

function toHuntMatchTargets(targets: HuntTargetUi[]) {
	return targets.map((t) =>
		t.kind === "asset"
			? { kind: "asset" as const, asset: t.asset }
			: { kind: "combo" as const, combo: t.combo },
	);
}

export default function LiquidationPage() {
	const [tab, setTab] = useState<Tab>("live");
	const { data: liveRaw = [], isLoading } = useGetPublicLiquidatableVaults();
	const { data: auctions = [], isLoading: auctionsLoading } =
		useGetLiquidationAuctions();
	const { data: riskData } = useProtocolRiskParams();
	const riskParams = toRiskParams(riskData);
	const legend = riskLegend(riskParams);
	const minCr = riskParams.minCollateralRatio;
	const liqT = riskParams.liquidationThreshold;
	const {
		isAuthenticated,
		login,
		isLoggingIn,
		principal,
		balances,
		refreshBalances,
		ensureProtocolActor,
		reportSessionIssue,
	} = useAuth();
	const { data: agent } = useMyAgent();
	const { data: huntTargets = [] } = useMyHuntTargets();
	const { data: huntHf } = useMyHuntHfRange();
	const [huntFilterOn, setHuntFilterOn] = useState(true);
	const qc = useQueryClient();
	const [target, setTarget] = useState<LiquidatableVaultUi | null>(null);
	const [repayInput, setRepayInput] = useState("");
	const [fullClear, setFullClear] = useState(false);
	const [stage, setStage] = useState<UpdateStage>("idle");
	const [pending, setPending] = useState(false);
	const [auctionPending, setAuctionPending] = useState<string | null>(null);

	const spendableUsdm = depositableTokenMax(balances.USDM);

	const live = useMemo(() => {
		if (
			!huntFilterOn ||
			!isAuthenticated ||
			!agent?.active ||
			huntTargets.length === 0
		) {
			return liveRaw;
		}
		const targets = toHuntMatchTargets(huntTargets);
		const minE8 = huntHf?.minE8 ?? 100_000_000n;
		const maxE8 = huntHf?.maxE8 ?? 130_000_000n;
		return liveRaw.filter(
			(v) =>
				vaultMatchesHunt(targets, v.collateralLines) &&
				vaultMatchesHfRange(v.cr, minE8, maxE8),
		);
	}, [
		liveRaw,
		huntFilterOn,
		isAuthenticated,
		agent?.active,
		huntTargets,
		huntHf?.minE8,
		huntHf?.maxE8,
	]);

	useEffect(() => {
		if (!target) return;
		const def = Math.min(target.maxPartialUsdm, spendableUsdm);
		setRepayInput(formatAmountPlain(def > 0 ? def : 0, 2));
		setFullClear(false);
	}, [target, spendableUsdm]);

	async function readyActor() {
		const pair = await ensureProtocolActor();
		if (!pair) {
			reportSessionIssue("degraded");
			throw new Error(
				"Protocol access missing — reconnect Internet Identity, then retry.",
			);
		}
		await ensureProtocolUser(pair.actor);
		return pair;
	}

	const totalDebt = useMemo(
		() => live.reduce((s, v) => s + v.debtUsdm, 0),
		[live],
	);
	const totalProfit = useMemo(
		() => live.reduce((s, v) => s + v.profitUsd, 0),
		[live],
	);

	async function executeLiquidate() {
		if (!target) return;
		if (!principal) {
			toast.error("Connect Internet Identity first");
			return;
		}
		const repayN = fullClear
			? target.payUsdm
			: parseAmount(repayInput);
		if (!fullClear && (!(repayN > 0) || repayN > target.maxPartialUsdm + 1e-9)) {
			toast.error(
				`Partial repay must be between 0 and ${formatNum(target.maxPartialUsdm, 2)} USDM (close factor 50%)`,
			);
			return;
		}
		const need = fullClear ? target.payUsdm : repayN;
		if (spendableUsdm + 1e-6 < need) {
			toast.error(
				`Need ~${formatNum(need, 2)} spendable USDM (wallet ${formatNum(balances.USDM, 2)} after fees)`,
			);
			return;
		}
		setPending(true);
		setStage("signing");
		try {
			if (target.source !== "live") {
				await runDemoUpdateStages(setStage);
				toast.success("Demo liquidate — connect + protocol env for live");
				setTarget(null);
				setStage("idle");
				return;
			}
			const pair = await readyActor();
			setStage("submitting");
			const config = getIcConfig();
			const liquidator = Principal.fromText(principal);
			const preview = await liquidateVault(
				pair.actor,
				pair.agent,
				config,
				target.id,
				{
					liquidator,
					fullClear,
					repayUsdm: fullClear ? undefined : repayN,
				},
			);
			setStage("confirmed");
			toast.success(
				`Liquidated #${target.id.toString()} · paid ${(Number(preview.debtE8) / 1e8).toFixed(2)} USDM · bonus ${(Number(preview.bonusBps) / 100).toFixed(1)}% — coll should appear in Holdings`,
			);
			void qc.invalidateQueries({ queryKey: ["v2", "public", "liquidatable"] });
			void qc.invalidateQueries({ queryKey: ["v2", "public", "dutchAuctions"] });
			void qc.invalidateQueries({ queryKey: ["v2", "public", "vaults"] });
			void qc.invalidateQueries({ queryKey: ["v2", "wallet", "activity"] });
			void qc.invalidateQueries({ queryKey: ["v2", "userVaults"] });
			void refreshBalances();
			setTimeout(() => {
				setTarget(null);
				setStage("idle");
			}, 500);
		} catch (err) {
			setStage("idle");
			if (isAuthPermissionError(err)) {
				reportSessionIssue("unauthorized");
				toast.error(
					"Protocol access missing — reconnect Internet Identity, then retry liquidate.",
				);
			} else {
				toast.error(humanizeIcError(err));
			}
		} finally {
			setPending(false);
		}
	}

	async function startDutch(vaultId: bigint) {
		if (!isAuthenticated) {
			void login();
			return;
		}
		setAuctionPending(`start-${vaultId}`);
		try {
			const pair = await readyActor();
			unwrapResult(
				await pair.actor.startLiquidationAuction(vaultId),
				"startLiquidationAuction",
			);
			toast.success(`Dutch auction started for vault #${vaultId.toString()}`);
			void qc.invalidateQueries({ queryKey: ["v2", "public", "dutchAuctions"] });
			void qc.invalidateQueries({ queryKey: ["v2", "public", "liquidatable"] });
			setTab("auctions");
		} catch (e) {
			if (isAuthPermissionError(e)) {
				reportSessionIssue("unauthorized");
				toast.error(
					"Protocol access missing — reconnect Internet Identity, then retry.",
				);
			} else {
				toast.error(humanizeIcError(e));
			}
		} finally {
			setAuctionPending(null);
		}
	}

	async function fillDutch(auctionId: bigint, currentPriceUsdm: number) {
		if (!isAuthenticated) {
			void login();
			return;
		}
		if (balances.USDM + 1e-6 < currentPriceUsdm) {
			toast.error(
				`Need ~${formatNum(currentPriceUsdm, 2)} USDM (wallet ${formatNum(balances.USDM, 2)})`,
			);
			return;
		}
		setAuctionPending(`fill-${auctionId}`);
		try {
			const pair = await readyActor();
			const config = getIcConfig();
			if (!principal) throw new Error("Connect first");
			await fillLiquidationAuction(
				pair.actor,
				pair.agent,
				config,
				auctionId,
				currentPriceUsdm,
				Principal.fromText(principal),
			);
			toast.success(`Filled auction #${auctionId.toString()}`);
			void qc.invalidateQueries({ queryKey: ["v2", "public", "dutchAuctions"] });
			void qc.invalidateQueries({ queryKey: ["v2", "public", "liquidatable"] });
			void qc.invalidateQueries({ queryKey: ["v2", "wallet", "activity"] });
			void qc.invalidateQueries({ queryKey: ["v2", "userVaults"] });
			void refreshBalances();
		} catch (e) {
			if (isAuthPermissionError(e)) {
				reportSessionIssue("unauthorized");
				toast.error(
					"Protocol access missing — reconnect Internet Identity, then retry.",
				);
			} else {
				toast.error(humanizeIcError(e));
			}
		} finally {
			setAuctionPending(null);
		}
	}

	function openLiquidate(v: LiquidatableVaultUi) {
		if (!isAuthenticated) {
			void login();
			toast.message("Connect to liquidate, then try again");
			return;
		}
		setTarget(v);
		setStage("idle");
	}

	return (
		<div className="pb-8">
			<div className="container mx-auto px-4 pt-6 sm:pt-8">
				<p className="section-eyebrow">Liquidate</p>
				<h1 className="text-xl font-bold tracking-tight sm:text-2xl">
					Keepers · fixed-spread &amp; Dutch
				</h1>
				<p className="mt-1 max-w-2xl text-sm text-muted-foreground">
					Pay vault debt in USDM, seize collateral. Open fixed-spread from{" "}
					<strong>{liqT.toFixed(2)}×</strong> CR (live protocol threshold);
					Dutch when collateral value &lt; debt (CR &lt; 1.00×). Mint floor{" "}
					<strong>{minCr.toFixed(2)}×</strong>.
				</p>
				<p className="mt-1 text-xs">
					<Link
						to={docsArticlePath("liquidation", "overview")}
						className="font-semibold text-primary underline"
					>
						Docs · how it works
					</Link>
				</p>

				{isAuthenticated && agent?.active && huntTargets.length > 0 ? (
					<div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
						<button
							type="button"
							onClick={() => setHuntFilterOn((v) => !v)}
							className={cn(
								"rounded-full border px-3 py-1 font-semibold",
								huntFilterOn
									? "border-primary bg-primary/15 text-primary"
									: "border-border text-muted-foreground",
							)}
						>
							{huntFilterOn
								? `Hunt filter on · ${live.length}/${liveRaw.length}`
								: "Hunt filter off · show all"}
						</button>
						<span className="text-muted-foreground">
							From Agent Layer hunt targets + HF band (singles + combos)
						</span>
					</div>
				) : null}

				<div className="mt-4 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
					{legend.map((r) => (
						<span
							key={r.badge}
							className={cn(
								"rounded-full border px-2 py-0.5",
								r.badgeClass,
							)}
						>
							<strong>{r.badge}</strong> · {r.range}
						</span>
					))}
				</div>

				<div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
					<div className="card-surface p-3">
						<p className="text-[10px] uppercase text-muted-foreground">
							Live targets
						</p>
						<p className="font-mono-amount text-xl font-bold">{live.length}</p>
					</div>
					<div className="card-surface p-3">
						<p className="text-[10px] uppercase text-muted-foreground">
							Debt to cover
						</p>
						<p className="font-mono-amount text-xl font-bold">
							${formatNum(totalDebt, 0)}
						</p>
					</div>
					<div className="card-surface p-3">
						<p className="text-[10px] uppercase text-muted-foreground">
							Est. profit
						</p>
						<p className="font-mono-amount text-xl font-bold text-primary">
							${formatNum(totalProfit, 0)}
						</p>
					</div>
					<div className="card-surface p-3">
						<p className="text-[10px] uppercase text-muted-foreground">
							Your USDM
						</p>
						<p className="font-mono-amount text-xl font-bold">
							{formatNum(balances.USDM, 2)}
						</p>
					</div>
				</div>

				<div className="mt-6 flex gap-1 rounded-full border border-border p-1 sm:w-fit">
					{(
						[
							["live", "Live liquidatable"],
							["auctions", "Dutch auctions"],
							["history", "History"],
						] as const
					).map(([id, label]) => (
						<button
							key={id}
							type="button"
							onClick={() => setTab(id)}
							className={cn(
								"min-h-10 rounded-full px-4 text-xs font-semibold",
								tab === id
									? "bg-primary/15 text-primary"
									: "text-muted-foreground",
							)}
						>
							{label}
						</button>
					))}
				</div>
			</div>

			<div className="container mx-auto space-y-4 px-4 py-6">
				{tab === "live" && (
					<ul className="grid gap-4 lg:grid-cols-2">
						{isLoading
							? Array.from({ length: 4 }).map((_, i) => (
									<li key={i} className="skeleton-shimmer h-56" />
								))
							: null}
						{!isLoading &&
							live.map((v) => {
								const risk = classifyVaultRisk(v.collateralUsd, v.debtUsdm, riskParams);
								return (
									<li
										key={String(v.id)}
										className={cn(
											"card-surface space-y-3 p-5",
											risk.borderClass,
											risk.bgClass,
										)}
									>
										<div className="flex items-start justify-between gap-3">
											<div>
												<p className="font-mono text-lg font-bold">
													Vault #{v.id.toString()}
												</p>
												<p className="text-xs text-muted-foreground">
													LTV {v.ltv.toFixed(1)}% · bonus {v.bonusPct}%
												</p>
											</div>
											<VaultRiskBadge
												meta={risk}
												size="lg"
											/>
										</div>

										<div>
											<p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
												Collateral locked
											</p>
											<CollateralLines lines={v.collateralLines} />
										</div>

										<div className="grid grid-cols-2 gap-3 border-t border-border/50 pt-3 sm:grid-cols-4">
											<div>
												<p className="text-[10px] font-semibold uppercase text-muted-foreground">
													Oracle value
												</p>
												<p className="font-mono-amount text-base font-bold">
													${formatNum(v.collateralUsd, 2)}
												</p>
											</div>
											<div>
												<p className="text-[10px] font-semibold uppercase text-muted-foreground">
													You pay
												</p>
												<p className="font-mono-amount text-base font-bold text-primary">
													{formatNum(v.payUsdm, 2)} USDM
												</p>
											</div>
											<div>
												<p className="text-[10px] font-semibold uppercase text-muted-foreground">
													Profit opp.
												</p>
												<p className="font-mono-amount text-base font-bold text-emerald-600">
													~${formatNum(v.profitUsd, 2)}
												</p>
											</div>
											<div>
												<p className="text-[10px] font-semibold uppercase text-muted-foreground">
													Debt
												</p>
												<TokenAmount
													id="USDM"
													value={v.debtUsdm}
													decimals={2}
													size="sm"
												/>
											</div>
										</div>

										<div className="flex flex-wrap gap-2 pt-1">
											{v.underwater ? (
												<Button
													variant="secondary"
													className="flex-1"
													loading={
														auctionPending === `start-${v.id.toString()}`
													}
													onClick={() => void startDutch(v.id)}
												>
													Start Dutch auction
												</Button>
											) : (
												<button
													type="button"
													className="btn-primary inline-flex flex-1 items-center justify-center gap-1.5 px-4 text-sm"
													disabled={isLoggingIn}
													onClick={() => openLiquidate(v)}
												>
													<Gavel className="size-4" />
													Liquidate · pay {formatNum(v.payUsdm, 0)} USDM
												</button>
											)}
										</div>
									</li>
								);
							})}
						{!isLoading && live.length === 0 ? (
							<li className="card-surface p-8 text-center text-sm text-muted-foreground">
								No liquidatable vaults right now.
							</li>
						) : null}
					</ul>
				)}

				{tab === "auctions" && (
					<ul className="grid gap-4 lg:grid-cols-2">
						{auctionsLoading
							? Array.from({ length: 2 }).map((_, i) => (
									<li key={i} className="skeleton-shimmer h-40" />
								))
							: null}
						{!auctionsLoading &&
							auctions.map((a) => (
								<li key={String(a.id)} className="card-surface space-y-3 border-purple-500/50 bg-purple-500/15 p-5">
									<div className="flex items-start justify-between">
										<div>
											<p className="font-mono text-lg font-bold">
												Auction #{a.id.toString()}
											</p>
											<p className="text-xs text-muted-foreground">
												Vault #{a.vaultId.toString()}
											</p>
										</div>
										<span className="rounded-full bg-purple-600/20 px-2.5 py-1 text-[10px] font-bold uppercase text-purple-800 dark:text-purple-300">
											Dutch
										</span>
									</div>
									<div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
										<div>
											<p className="text-[10px] uppercase text-muted-foreground">
												You pay now
											</p>
											<p className="font-mono-amount text-lg font-bold text-primary">
												{formatNum(a.currentPriceUsdm, 2)} USDM
											</p>
										</div>
										<div>
											<p className="text-[10px] uppercase text-muted-foreground">
												Start → min
											</p>
											<p className="font-mono-amount text-sm font-semibold">
												{formatNum(a.startPriceUsdm, 2)} →{" "}
												{formatNum(a.minPriceUsdm, 2)}
											</p>
										</div>
										<div>
											<p className="text-[10px] uppercase text-muted-foreground">
												Discount
											</p>
											<p className="font-mono-amount text-lg font-bold">
												{a.discountPct.toFixed(1)}%
											</p>
										</div>
									</div>
									<Button
										fullWidth
										loading={auctionPending === `fill-${a.id.toString()}`}
										onClick={() =>
											void fillDutch(a.id, a.currentPriceUsdm)
										}
									>
										Fill auction · {formatNum(a.currentPriceUsdm, 2)} USDM
									</Button>
								</li>
							))}
						{!auctionsLoading && auctions.length === 0 ? (
							<li className="card-surface p-8 text-center text-sm text-muted-foreground">
								No open Dutch auctions. Underwater vaults can start one from the
								Live tab.
							</li>
						) : null}
					</ul>
				)}

				{tab === "history" && (
					<div className="space-y-3">
						<p className="text-sm text-muted-foreground">
							Your liquidation-related protocol events (and ledger transfers when
							index env is set).
						</p>
						<WalletActivityPanel initialCategory="liquidation" />
					</div>
				)}
			</div>

			<ConfirmSheet
				open={target !== null}
				onOpenChange={(o) => {
					if (!o && !pending) {
						setTarget(null);
						setStage("idle");
					}
				}}
				title={
					target
						? `Liquidate vault #${String(target.id)}`
						: "Liquidate vault"
				}
				description="Pre-pay USDM into escrow, then seize collateral. Partial repay (default) uses the 50% close factor and leaves residual debt/coll for other keepers. Full clear only when you intentionally repay all debt."
				confirmLabel={
					target
						? fullClear
							? `Full clear · ${formatNum(target.payUsdm, 2)} USDM`
							: `Partial · ${formatNum(parseAmount(repayInput) || 0, 2)} USDM`
						: "Confirm liquidate"
				}
				onConfirm={() => void executeLiquidate()}
				isPending={pending}
				stage={stage}
				destructive
			>
				{target ? (
					<div className="space-y-4 text-sm">
						<div className="flex justify-end">
							<VaultRiskBadge
								collateralUsd={target.collateralUsd}
								debtUsdm={target.debtUsdm}
								size="md"
							/>
						</div>
						<div>
							<p className="mb-1 text-[11px] font-semibold uppercase text-muted-foreground">
								Collateral locked
							</p>
							<CollateralLines lines={target.collateralLines} />
						</div>
						<div className="grid grid-cols-2 gap-2 rounded-xl border border-border bg-muted/30 p-3">
							<div>
								<p className="text-[10px] uppercase text-muted-foreground">
									Debt
								</p>
								<p className="font-mono-amount font-bold">
									{formatNum(target.debtUsdm, 2)} USDM
								</p>
							</div>
							<div>
								<p className="text-[10px] uppercase text-muted-foreground">
									Max partial (50%)
								</p>
								<p className="font-mono-amount font-bold text-primary">
									{formatNum(target.maxPartialUsdm, 2)} USDM
								</p>
							</div>
							<div>
								<p className="text-[10px] uppercase text-muted-foreground">
									Spendable USDM
								</p>
								<p className="font-mono-amount font-bold">
									{formatNum(spendableUsdm, 2)}
								</p>
							</div>
							<div>
								<p className="text-[10px] uppercase text-muted-foreground">
									Full-clear profit
								</p>
								<p className="font-mono-amount font-bold text-emerald-600">
									~${formatNum(target.profitUsd, 2)}
								</p>
							</div>
						</div>

						<label className="flex items-center gap-2 text-xs font-semibold">
							<input
								type="checkbox"
								checked={fullClear}
								onChange={(e) => setFullClear(e.target.checked)}
								className="size-4 rounded border-border"
							/>
							Full clear (repay all debt &amp; close vault — needs more USDM)
						</label>
						{!fullClear ? (
							<p className="text-[11px] text-muted-foreground">
								Partial keeps the vault open with remaining debt so other keepers
								can liquidate the rest.
							</p>
						) : null}

						{!fullClear ? (
							<label className="block">
								<span className="text-xs font-semibold text-muted-foreground">
									USDM to repay (partial)
								</span>
								<input
									value={repayInput}
									onChange={(e) => setRepayInput(e.target.value)}
									inputMode="decimal"
									className="mt-1 h-11 w-full rounded-xl border border-border bg-background px-3 font-mono-amount text-sm"
								/>
								<div className="mt-1.5 flex gap-2">
									<button
										type="button"
										className="text-[11px] font-semibold text-primary"
										onClick={() =>
											setRepayInput(
												formatAmountPlain(
													Math.min(target.maxPartialUsdm, spendableUsdm),
													2,
												),
											)
										}
									>
										Max partial
									</button>
									<button
										type="button"
										className="text-[11px] font-semibold text-primary"
										onClick={() =>
											setRepayInput(
												formatAmountPlain(
													Math.min(target.maxPartialUsdm / 2, spendableUsdm),
													2,
												),
											)
										}
									>
										50% of partial
									</button>
								</div>
							</label>
						) : null}

						<p className="text-[11px] text-muted-foreground">
							USDM is transferred into a protocol escrow for your principal,
							then burned with the liquidation. Seized collateral is sent to
							your wallet.
						</p>
					</div>
				) : null}
			</ConfirmSheet>
		</div>
	);
}
